// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { supabase, resetAuthSession } from '../services/supabase';

const AuthContext = createContext({});

// Timeout wrapper — prevents role fetch from hanging forever
const withTimeout = (promise, ms = 8000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Role fetch timeout')), ms)
    ),
  ]);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  const roleLoadedRef = useRef(false);
  const fetchingRoleRef = useRef(false);
  const sessionCorruptRef = useRef(false);
  const initCompleteRef = useRef(false);

  const fetchRole = useCallback(async (userId) => {
    if (!userId) return;
    if (fetchingRoleRef.current) return;
    if (sessionCorruptRef.current) return;

    fetchingRoleRef.current = true;
    setRoleError(null);

    try {
      const { data, error } = await withTimeout(
        supabase
          .from('profiles')
          .select('role')
          .eq('id', userId)
          .single()
      );

      if (error) {
        if (error.code === 'PGRST116') {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const metadata = authUser?.user_metadata;
          const fallbackRole = metadata?.role || 'agent';

          const { error: insertError } = await supabase
            .from('profiles')
            .insert([{
              id: userId,
              email: authUser?.email,
              full_name: metadata?.full_name || '',
              phone: metadata?.phone || '',
              role: fallbackRole
            }]);

          if (!insertError) {
            setUserRole(fallbackRole);
            roleLoadedRef.current = true;
            return;
          }
        }

        console.error('Error fetching role:', error);
        setUserRole(null);
        setRoleError(error.message || 'Could not load profile');
        roleLoadedRef.current = false;
      } else {
        setUserRole(data?.role ?? null);
        roleLoadedRef.current = true;
      }
    } catch (err) {
      console.error('fetchRole exception:', err.message);

      if (err.message === 'Role fetch timeout' || err.message?.includes('JWT')) {
        console.warn('Stale session detected — flagging as corrupt');
        sessionCorruptRef.current = true;
        resetAuthSession().catch(() => {});
        setUser(null);
        setUserRole(null);
        setRoleError('Your session expired. Please log in again.');
      } else {
        setUserRole(null);
        setRoleError(err.message || 'Unexpected error loading profile');
      }

      roleLoadedRef.current = false;
    } finally {
      fetchingRoleRef.current = false;
      setLoading(false);
    }
  }, []);

  const retryRole = useCallback(() => {
    if (user?.id) {
      sessionCorruptRef.current = false;
      roleLoadedRef.current = false;
      fetchRole(user.id);
    }
  }, [user, fetchRole]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        let { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          if (mounted) {
            setUser(null);
            setUserRole(null);
            setLoading(false);
          }
          return;
        }

        // Refresh the token
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData?.session) {
          session = refreshData.session;
        }

        const currentUser = session?.user ?? null;

        if (!mounted || !currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Direct fetch — bypasses Supabase JS client which can hang during token swap
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?select=role&id=eq.${currentUser.id}`,
          {
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`,
            }
          }
        );
        const result = await response.json();

        if (result?.[0]?.role) {
          setUserRole(result[0].role);
          roleLoadedRef.current = true;
        } else {
          setRoleError('Could not load role');
        }

        setLoading(false);
      } catch (e) {
        console.error('Init error:', e);
        if (mounted) {
          setRoleError('Failed to initialise session');
          setLoading(false);
        }
      } finally {
        initCompleteRef.current = true;
      }
    };

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Don't process events until init() has completed
        if (!initCompleteRef.current) return;

        if (sessionCorruptRef.current) {
          setLoading(false);
          return;
        }

        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (!currentUser) {
          setUserRole(null);
          roleLoadedRef.current = false;
          setLoading(false);
          return;
        }

        // Only fetch role for genuine sign-in events, not token refreshes
        if (event === 'SIGNED_IN' && !roleLoadedRef.current) {
          await fetchRole(currentUser.id);
        }

        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchRole]);

  const signIn = async (email, password) => {
    sessionCorruptRef.current = false;
    initCompleteRef.current = true;
    setRoleError(null);
    return await supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    setLoading(true);
    sessionCorruptRef.current = true;

    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (_) {}

    try {
      await resetAuthSession();
    } catch (_) {}

    setUser(null);
    setUserRole(null);
    setRoleError(null);
    roleLoadedRef.current = false;
    sessionCorruptRef.current = false;
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      userRole,
      loading,
      roleError,
      signIn,
      signOut,
      retryRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};